/* eslint-disable no-console */

import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';

import {
  configureDatadog,
  getDatadogLogger,
} from '@storebrand-digital/common-datadog';

import artifactInfo from '../../../artifactInfo.json';

import { type MonitoringLogger, type BrowserLogger } from './types';
import {
  addNonEnumerableProperty,
  extractFromAxiosError,
  getExceptionMessage,
} from './utils';

const datadogLogger = getDatadogLogger();

function log(
  exception: unknown,
  monitoringLogger: MonitoringLogger,
  browserLogger: BrowserLogger
) {
  const message = getExceptionMessage(exception);
  const environment = window.env.ENVIRONMENT;

  try {
    if (!['CLIENT-T', 'CLIENT-TS', 'PROD'].includes(window.env.ENVIRONMENT)) {
      console.info(`Monitor logging disabled for "${environment}" environment`);
      return;
    }

    const errorObject = exception instanceof Error ? exception : undefined;
    monitoringLogger(message, errorObject);
  } catch (e) {
    console.error(
      `Sending exception to monitoring platform failed: ${getExceptionMessage(
        e
      )}`
    );
  } finally {
    browserLogger(message);
  }
}

function errorLogger(message: string, error?: Error) {
  let dataDogContext = {};
  let sentryHint = {};

  if (error instanceof AxiosError) {
    const { correlationId, responseData, requestUrl } =
      extractFromAxiosError(error);

    dataDogContext = {
      axios_custom_context: {
        request_url: requestUrl,
        response_data: responseData,
        x_correlation_id: correlationId,
      },
    };

    // add Sentry flag to skip internal normalization of the object
    addNonEnumerableProperty(
      responseData,
      '__sentry_skip_normalization__',
      true
    );

    sentryHint = {
      contexts: {
        'Axios Custom Context': {
          'Response data': responseData,
        },
      },
      tags: {
        request_url: requestUrl,
        x_correlation_id: correlationId,
      },
    };
  }

  datadogLogger.error(message, dataDogContext, error);

  Sentry.captureException(error ?? message, {
    level: 'error',
    ...sentryHint,
  });
}

/**
 * Use console.error in the browser and send exceptions to the monitoring platform
 *
 * @param exception Expects an `Error` object.
 * It's also possible to pass non-`Error` objects and strings,
 * but be aware that the resulting events in Sentry may be missing a stack trace.
 */
export function logError(exception: unknown) {
  log(exception, errorLogger, console.error);
}

function warnLogger(message: string, error?: Error) {
  datadogLogger.warn(message, {}, error);
  Sentry.captureException(error ?? message, {
    level: 'warning',
  });
}

/**
 * Use console.warn in the browser and send exceptions to the monitoring platform
 *
 * @param exception Expects an `Error` object.
 * It's also possible to pass non-`Error` objects and strings,
 * but be aware that the resulting events in Sentry may be missing a stack trace.
 */
export const logWarn = (exception: unknown) => {
  log(exception, warnLogger, console.warn);
};
// TODO: fix client-ts env.
export function initMonitoring() {
  if (['CLIENT-T', 'CLIENT-TS', 'PROD'].includes(window.env.ENVIRONMENT)) {
    Sentry.init({
      dsn: window.env.SENTRY_DNS,
      environment: window.env.ENVIRONMENT,
      integrations: [Sentry.browserTracingIntegration()],
      release: artifactInfo.sentryRelease,
      tracesSampleRate: window.env.ENVIRONMENT === 'PROD' ? 0.25 : 1.0,
    });
    configureDatadog({
      appId: window.env.DATADOG_APP_ID,
      appName: window.env.CLIENT_ID,
      appVersion: artifactInfo.packageVersion,
      clientPublicToken: window.env.DATADOG_PUBLIC_TOKEN,
      configureLogging: true,
      environment:
        window.env.ENVIRONMENT === 'PROD' ? 'production' : 'client-t',
    });
  } else {
    console.info(
      `Monitoring disabled for "${window.env.ENVIRONMENT}" environment`
    );
  }
}
